import { Vendor, VendorOperatingHour } from '../models/vendor.model';
import { isFoodVendorBusinessType, isGroceryVendorBusinessType } from './vendor-business-type.util';
import tzLookup from 'tz-lookup';

function getVendorCoordinates(vendor: Vendor): { lat: number; lng: number } | null {
  const lat = vendor.latitude;
  const lng = vendor.longitude;
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
    return null;
  }
  if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) {
    return null;
  }
  return { lat: latNum, lng: lngNum };
}

function getVendorTimeZoneFromCoordinates(vendor: Vendor): string | undefined {
  const coords = getVendorCoordinates(vendor);
  if (!coords) return undefined;
  try {
    return tzLookup(coords.lat, coords.lng);
  } catch {
    return undefined;
  }
}

function getNowInVendorTime(vendor: Vendor): { day: number; minutes: number } {
  const timezone = getVendorTimeZoneFromCoordinates(vendor);
  if (!timezone) {
    const now = new Date();
    return { day: now.getDay(), minutes: now.getHours() * 60 + now.getMinutes() };
  }

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(new Date());
    const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
    const dayMap: Record<string, number> = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    const day = dayMap[weekday] ?? new Date().getDay();
    return { day, minutes: hour * 60 + minute };
  } catch {
    const now = new Date();
    return { day: now.getDay(), minutes: now.getHours() * 60 + now.getMinutes() };
  }
}

/**
 * Check if vendor is currently open based on operating hours
 */
export function isVendorOpen(vendor: Vendor): boolean {
  if (!vendor.isActive) {
    return false;
  }

  if (!vendor.operatingHours || vendor.operatingHours.length === 0) {
    // If no operating hours defined, assume always open if active
    return true;
  }

  const nowInVendorTime = getNowInVendorTime(vendor);
  const currentDay = nowInVendorTime.day; // 0 = Sunday, 6 = Saturday
  const currentTime = nowInVendorTime.minutes; // Time in minutes

  const todayHours = vendor.operatingHours.find((hours) => Number(hours.day) === currentDay);

  if (!todayHours || todayHours.isClosed) {
    return false;
  }

  const [openHour, openMin] = todayHours.open.split(':').map(Number);
  const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
  const openTime = openHour * 60 + openMin;
  const closeTime = closeHour * 60 + closeMin;

  // Handle closing time past midnight
  if (closeTime < openTime) {
    return currentTime >= openTime || currentTime <= closeTime;
  }

  return currentTime >= openTime && currentTime <= closeTime;
}

/**
 * Get vendor status text (Open/Closed)
 */
export function getVendorStatusText(vendor: Vendor): string {
  if (!vendor.isActive) {
    return 'Closed';
  }

  if (isVendorOpen(vendor)) {
    return 'Open';
  }

  // Find next opening time
  const nowInVendorTime = getNowInVendorTime(vendor);
  const currentDay = nowInVendorTime.day;
  const currentTime = nowInVendorTime.minutes;

  if (!vendor.operatingHours || vendor.operatingHours.length === 0) {
    return 'Open';
  }

  // Check today's hours
  const todayHours = vendor.operatingHours.find((hours) => Number(hours.day) === currentDay);
  if (todayHours && !todayHours.isClosed) {
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
    const closeTime = closeHour * 60 + closeMin;
    if (currentTime < closeTime) {
      return `Closes at ${todayHours.close}`;
    }
  }

  // Find next day with hours
  for (let i = 1; i <= 7; i++) {
    const checkDay = (currentDay + i) % 7;
    const dayHours = vendor.operatingHours.find((hours) => Number(hours.day) === checkDay);
    if (dayHours && !dayHours.isClosed) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return `Opens ${dayNames[checkDay]} at ${dayHours.open}`;
    }
  }

  return 'Closed';
}

// Zone operating hours — timezone-aware (see zone-time.utils.ts)
export {
  isZoneOpen,
  getZoneStatusText,
  getZoneClosedMessage,
  getZoneClosedMessageParts,
  resolveServiceZoneTimeZone,
  formatServiceZoneTimeZoneLabel,
} from './zone-time.utils';

// ─── Vendor utils ─────────────────────────────────────────────────────────────

/**
 * Whether a store may appear in customer-facing listings.
 *
 * `isActive` is the admin visibility switch and always wins: deactivating a store in the
 * admin module hides it everywhere. `status` only describes how the vendor is trading —
 * a paused store stays listed (rendered disabled) because pausing keeps `isActive = true`,
 * while a closed store is hidden.
 */
export function isVendorVisibleToCustomers(vendor: Vendor | null | undefined): boolean {
  if (!vendor) return false;
  if (vendor.isActive === false) return false;
  if (vendor.status === 'closed') return false;
  return true;
}

/**
 * Get vendor badges based on tags and ratings
 */
export function getVendorBadges(vendor: Vendor): string[] {
  const badges: string[] = [];

  if (vendor.tags) {
    badges.push(...vendor.tags);
  }

  if (vendor.rating && vendor.rating >= 4.5) {
    badges.push('Top Rated');
  }

  if (vendor.ratingCount && vendor.ratingCount < 10) {
    badges.push('New');
  }

  // Check if pure veg (would need additional field or tag)
  if (vendor.tags?.includes('Pure Veg') || vendor.tags?.includes('Vegetarian')) {
    badges.push('Pure Veg');
  }

  return badges.slice(0, 3); // Limit to 3 badges
}

/**
 * Format delivery fee for display
 */
export function formatDeliveryFee(fee?: string | number): string {
  if (!fee) {
    return 'Free delivery';
  }

  if (typeof fee === 'number') {
    if (fee === 0) {
      return 'Free delivery';
    }
    return `₹${fee.toFixed(0)} delivery`;
  }

  if (fee === '0' || fee === 'Free' || fee.toLowerCase() === 'free') {
    return 'Free delivery';
  }

  return fee;
}

/**
 * Check if vendor is a grocery vendor based on businessType
 */
export function isGroceryVendor(vendor: Vendor | null | undefined): boolean {
  return isGroceryVendorBusinessType(vendor?.businessType);
}

/**
 * Check if vendor is a food vendor based on businessType
 */
export function isFoodVendor(vendor: Vendor | null | undefined): boolean {
  return isFoodVendorBusinessType(vendor?.businessType);
}








